import { ActionSheetController, AlertController, ModalController, Platform } from '@ionic/angular';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, filter, firstValueFrom, map, Observable, Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import {
  asksForNotifications,
  CgvSelectors,
  CheckNewCgvVersion,
  LoadUserProfileAction,
  SetGroupIdCreateEventAction,
  UserProfileSelector,
} from '@smatchy/core';
import { GetChatsAction, MessagingSelector } from '@smatchy/domains/message';
import { whatTheMessagesTabSaysIsUnread } from '../../../../../core/domains/message/domains/what-the-messages-tab-says-is-unread';
import { LocationAccessService } from '../../../../../core/domains/user/services/location-access.service';
import { LocationPermissionModalPage } from '../../../../components/design-system/organisms/location-permission-modal/location-permission-modal.component';
import { DeviceLocationOffAlertComponent } from '../../../../components/design-system/organisms/device-location-off-alert/device-location-off-alert.component';
import { NotificationModalPage } from '../../../../components/design-system/organisms/notification-modale/notification-modale.component';
import { PushNotificationAdapter } from '../../../../../adapters/push-notification.adapter';
import { App } from '@capacitor/app';
import { AppIconBadgeService } from '../../../../../core/services/app-icon-badge.service';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { TermCommissionPolicyModalComponent } from '../../../../components/design-system/organisms/term-commission-policy/term-commission-policy.component';
import { TermGcsPolicyModalComponent } from '../../../../components/design-system/organisms/term-gcs-policy/term-gcs-policy.component';
import type { EnableLocationFailureReason } from '../../../../../core/domains/user/services/location-access.service';
import { GCS_ACCEPTED_ROLE } from '../../../../components/design-system/organisms/term-gcs-policy/term-gcs-policy.component';
import {
  LEGAL_TEXT_AGREED_ROLE,
  LEGAL_TEXT_POSTPONED_ROLE,
  TermLegalTextUpdateModalComponent,
} from '../../../../components/design-system/organisms/term-legal-text-update/term-legal-text-update.component';
import type { LocationPermissionModalMode } from '../../../../components/design-system/organisms/location-permission-modal/location-permission-modal.component';
export interface MenuItem {
  icon: string;
  path: string;
  label: string;
}

@Component({
  selector: 'smatchy-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false,
})
export class HomeComponent implements OnInit, OnDestroy {
  private pagesToHiderTabBar = [
    '/create-event',
    '/detail-activity',
    '/edit-activity',
    '/create-group',
    '/delete-group',
    '/unable-to-delete-group',
    '/edit-group',
    '/group',
    '/profile/my-groups',
    '/message/discussion',
    '/home/profile/sport',
    '/home/delete-sport',
    '/home/delete-user',
    '/all-invitations',
  ];
  private pagesStrictToHiderTabBar = ['/home/search'];
  #appResumeListener?: PluginListenerHandle;
  #appStateListener?: PluginListenerHandle;
  #locationSettingsSub?: Subscription;
  #isHandlingOsLocationNotice = false;
  #foregroundCheckInFlight?: Promise<void>;
  #lastForegroundCheckAt = 0;
  /** True after Control Center / Quick Settings / app switch made us inactive. */
  #needsOsLocationRecheckAfterInactive = false;
  #navigationSub?: Subscription;

  /**
   * True while a router navigation is in flight. The tab bar binds its buttons
   * to `[disabled]="navigating"` so a second tap cannot start a competing
   * navigation on the same primary outlet.
   *
   * CAPACITOR-Q: the tab bar was the last unprotected navigation surface.
   * #468 (route order) and the profile-sports page `navigating` guard each
   * fixed one screen, but tapping a tab while a route transition was still
   * resolving started a second activation and threw « Cannot activate an
   * already activated outlet », crashing the app. The worst case is navigating
   * to a Home sibling such as /on-boarding-sport, which tears HomeComponent
   * and its tabs outlet down: a tab tap mid-teardown reactivated an outlet
   * that was being destroyed. One navigation at a time.
   */
  navigating = false;

  /** What the red dot on the Messages tab reads, or nothing. */
  readonly unreadMessages$: Observable<string | null>;

  constructor(
    private readonly _router: Router,
    private readonly store: Store,
    private actionSheetCtrl: ActionSheetController,
    private translate: TranslateService,
    private readonly _platform: Platform,
    private modalController: ModalController,
    private readonly alertController: AlertController,
    private readonly _pushNotificationAdapter: PushNotificationAdapter,
    private readonly _locationAccessService: LocationAccessService,
    private readonly _appIconBadgeService: AppIconBadgeService,
  ) {
    this.unreadMessages$ = this.store
      .select(MessagingSelector.unreadMessagesCount)
      .pipe(map(whatTheMessagesTabSaysIsUnread));
  }

  ngOnInit() {
    // A type predicate rather than a boolean and an annotation below it: the
    // boolean filtered at runtime and narrowed nothing, so the annotation was
    // a promise the compiler could not check.
    this._router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((event) => {
      if (this.pagesToHiderTabBar.find((v) => event.url.includes(v))) {
        this.hideTabBar();
      } else if (this.pagesStrictToHiderTabBar.find((v) => event.url.toLowerCase() === v.toLowerCase())) {
        this.hideTabBar();
      } else {
        this.showTabBar();
      }
    });
    // CAPACITOR-Q: mirror the router's navigation lifecycle onto `navigating`
    // so the tab bar disables itself for the whole in-flight window. A tab tap
    // only ever fires when idle, so we never block a navigation the user
    // actually wants. End/Cancel/Error all re-enable the bar, so it can never
    // stay stuck disabled after a redirect or a cancelled navigation.
    this.#navigationSub = this._router.events
      .pipe(
        filter(
          (e): e is NavigationStart | NavigationEnd | NavigationCancel | NavigationError =>
            e instanceof NavigationStart ||
            e instanceof NavigationEnd ||
            e instanceof NavigationCancel ||
            e instanceof NavigationError,
        ),
      )
      .subscribe((event) => {
        this.navigating = event instanceof NavigationStart;
      });
    this.#locationSettingsSub = this._locationAccessService.locationSettingsChanged$.subscribe(() => {
      if (this._locationAccessService.peekOsUnavailableNoticePending()) {
        void this.promptOsLocationUnavailableNotice();
      }
    });
    void this.bootstrapLocationAndNotifications();
    this.checkCgvNew();
    // 28/10/2026: the conversations were only loaded once the Messages tab was
    // opened, so its red dot could never tell anyone to open it. Loaded once
    // here; a network failure only leaves the dot off.
    this.store
      .dispatch(new GetChatsAction())
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  ngOnDestroy() {
    this.#navigationSub?.unsubscribe();
    this.#locationSettingsSub?.unsubscribe();
    void this.#appResumeListener?.remove();
    void this.#appStateListener?.remove();
  }

  requestNotificationPermission = async () => {
    const modal = await this.modalController.create({
      component: NotificationModalPage,
    });
    await modal.present();
    return await modal.onDidDismiss();
  };

  requestLocationPermission = async (options?: {
    mode?: LocationPermissionModalMode;
    blockReason?: EnableLocationFailureReason;
  }) => {
    const mode = options?.mode ?? 'enable';
    const blockReason = options?.blockReason ?? 'permission_denied';
    const existingModal = await this.modalController.getTop();

    if (existingModal?.id === 'location-permission-modal') {
      const result = await existingModal.onDidDismiss();
      return this.handleLocationPermissionModalResult(result);
    }

    const modal = await this.modalController.create({
      id: 'location-permission-modal',
      cssClass: 'location-permission-modal',
      component: LocationPermissionModalPage,
      componentProps: {
        mode,
        blockReason,
      },
    });
    await modal.present();
    const result = await modal.onDidDismiss();
    return this.handleLocationPermissionModalResult(result);
  };

  private async handleLocationPermissionModalResult(result: {
    role?: string;
    data?: { blockReason?: EnableLocationFailureReason };
  }) {
    if (result.role === 'open_settings') {
      const reason =
        result.data?.blockReason ?? this._locationAccessService.getLastEnableFailureReason() ?? 'permission_denied';

      if (reason === 'location_services_disabled') {
        await this._locationAccessService.openDeviceLocationSettings();
      } else {
        await this._locationAccessService.openAppLocationPermissionSettings();
      }

      return { role: 'open_settings', data: false };
    }

    if (result.role === 'enable_requested') {
      return this.finishLocationEnableAfterModalDismiss();
    }

    return result;
  }

  private async finishLocationEnableAfterModalDismiss() {
    await this.waitForModalDismissAnimation();

    const isEnabled = await this._locationAccessService.enableLocationAccess();

    if (isEnabled) {
      return { role: 'confirm', data: true };
    }

    const reason = this._locationAccessService.getLastEnableFailureReason();

    if (!this._locationAccessService.shouldOpenSettingsForLocationFailure()) {
      return { role: 'pending', data: false };
    }

    if (reason === 'location_services_disabled') {
      return { role: 'pending', data: false };
    }

    await this._locationAccessService.openAppLocationPermissionSettings();

    return { role: 'cancel', data: false };
  }

  private waitForModalDismissAnimation(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, 600);
    });
  }

  private waitForLocationServicesWarmup(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, 450);
    });
  }

  async presentActionSheet() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: this.translate.instant('TABS.CREATE_EVENT.TITLE'),
      cssClass: 'create-action-sheet',
      buttons: [
        {
          cssClass: 'button-events',
          text: this.translate.instant('TABS.CREATE_EVENT.BUTTONS.EVENT'),
          icon: '/assets/images/icons/medal.svg',
          handler: () => {
            this.store.dispatch(new SetGroupIdCreateEventAction(''));
            this._router.navigate(['home/create-event']);
          },
        },
        {
          cssClass: 'button-users',
          text: this.translate.instant('TABS.CREATE_EVENT.BUTTONS.GROUP'),
          icon: '/assets/images/icons/users.svg',
          handler: () => {
            this._router.navigate(['home/create-group']);
          },
        },
        {
          cssClass: 'button-close',
          icon: '/assets/images/icons/cross.svg',
          handler: async () => {
            await actionSheet.dismiss();
          },
        },
      ],
    });

    await actionSheet.present();
    // subscribe backButton to hide action sheet on press it
    const subscription = this._platform.backButton.subscribeWithPriority(100, async () => {
      // hide the action sheet
      await actionSheet.dismiss();
    });
    // unsubscribe backButton on action sheet dismiss
    actionSheet.onWillDismiss().then(() => {
      subscription.unsubscribe();
    });
  }
  private async checkCgvNew() {
    // The check is awaited here, beside the profile, rather than dispatched
    // and forgotten in ngOnInit: the windows below read its answer, and until
    // 22/10/2026 the commission one could read the state before it arrived.
    // A failed check answers nothing new, as it always has.
    await Promise.all([
      firstValueFrom(this.store.dispatch(new LoadUserProfileAction())),
      firstValueFrom(this.store.dispatch(new CheckNewCgvVersion())).catch(() => undefined),
    ]);

    let profile = this.store.selectSnapshot(UserProfileSelector.profile);

    // Priority: if user still has not accepted the latest GCS, show it first.
    if (profile?.acceptedGcs !== true) {
      const modal = await this.modalController.create({
        component: TermGcsPolicyModalComponent,
        cssClass: 'term-commission-policy-modal',
        backdropDismiss: false,
        // backdropDismiss does not cover the Android hardware back button,
        // which dismisses overlays on its own. Only the dismissal the modal
        // itself performs, once the API has recorded the acceptance, carries
        // this role.
        canDismiss: (_data?: unknown, role?: string) => Promise.resolve(role === GCS_ACCEPTED_ROLE),
        handle: false,
        breakpoints: [1],
        initialBreakpoint: 1,
      });

      await modal.present();
      await modal.onDidDismiss();

      // Refresh profile after acceptance (or closing) to avoid opening multiple modals.
      await firstValueFrom(this.store.dispatch(new LoadUserProfileAction()));
      profile = this.store.selectSnapshot(UserProfileSelector.profile);
    }

    // The terms of use, then the privacy policy, each one after the other, after
    // the sale conditions. Same guard as the sale conditions: only the window
    // itself closes the window.
    for (const text of this.store.selectSnapshot(CgvSelectors.textsToPutToTheMember) ?? []) {
      const modal = await this.modalController.create({
        component: TermLegalTextUpdateModalComponent,
        componentProps: { text },
        cssClass: 'term-commission-policy-modal',
        backdropDismiss: false,
        canDismiss: (_data?: unknown, role?: string) =>
          Promise.resolve(role === LEGAL_TEXT_AGREED_ROLE || role === LEGAL_TEXT_POSTPONED_ROLE),
        handle: false,
        breakpoints: [1],
        initialBreakpoint: 1,
      });
      await modal.present();
      await modal.onDidDismiss();
    }

    const newCgv = await firstValueFrom(this.store.select(CgvSelectors.checkNewCommissionVersion));

    if (profile?.premiumRole?.isActive && (!profile?.acceptedCommission || newCgv)) {
      const modal = await this.modalController.create({
        component: TermCommissionPolicyModalComponent,
        cssClass: 'term-commission-policy-modal',
        backdropDismiss: false,
        handle: false,
        breakpoints: [1],
        initialBreakpoint: 1,
      });
      return await modal.present();
    }
  }
  private async bootstrapLocationAndNotifications(): Promise<void> {
    await this._locationAccessService.reconcileLocationState();
    this._locationAccessService.resetExploreSearchToGpsDefaultsOnColdStart();

    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await this.initNotificationProcess();
    await this.registerLocationAccessResumeListener();
  }

  private async initNotificationProcess() {
    await this._pushNotificationAdapter.addListener();
    await firstValueFrom(this.store.dispatch(new LoadUserProfileAction()));
    await this._appIconBadgeService.start();
    const theApiHasAToken = this.store.selectSnapshot(UserProfileSelector.hasPushToken);
    const theSystemSaidYes = await this._pushNotificationAdapter.isPermitted();
    if (asksForNotifications({ theApiHasAToken, theSystemSaidYes })) {
      await this.requestNotificationPermission();
    } else {
      await this._pushNotificationAdapter.registerIfPermitted();
    }
    await this._appIconBadgeService.syncFromBackend();
    await this.promptLocationAccessIfNeeded();
  }

  private async registerLocationAccessResumeListener(): Promise<void> {
    this.#appResumeListener = await App.addListener('resume', () => {
      void this.handleAppForegroundLocationCheck({ force: true });
    });
    this.#appStateListener = await App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        // Control Center / notification shade / app switch — recheck when
        // the user comes back (they may have toggled Location there).
        this.#needsOsLocationRecheckAfterInactive = true;
        return;
      }

      const force = this.#needsOsLocationRecheckAfterInactive;
      this.#needsOsLocationRecheckAfterInactive = false;
      void this.handleAppForegroundLocationCheck({ force });
    });
  }

  private async handleAppForegroundLocationCheck(options?: { force?: boolean }): Promise<void> {
    if (this.#isHandlingOsLocationNotice) {
      return;
    }

    if (this.#foregroundCheckInFlight) {
      return this.#foregroundCheckInFlight;
    }

    const now = Date.now();
    if (!options?.force && now - this.#lastForegroundCheckAt < 800) {
      return;
    }

    this.#lastForegroundCheckAt = now;
    this.#foregroundCheckInFlight = this.runAppForegroundLocationCheck().finally(() => {
      this.#foregroundCheckInFlight = undefined;
    });

    return this.#foregroundCheckInFlight;
  }

  private async runAppForegroundLocationCheck(): Promise<void> {
    if (this._locationAccessService.isPendingEnableAfterOsLocationServices()) {
      await this.waitForLocationServicesWarmup();
      const blockReason = await this._locationAccessService.getOsLocationBlockReason({
        probeIfGranted: true,
      });

      if (blockReason !== 'none') {
        this._locationAccessService.notifyOsLocationBlocked(blockReason);
        await this.promptOsLocationUnavailableNotice();
        return;
      }

      await this._locationAccessService.resumePendingLocationEnable();
      return;
    }

    if (!this._locationAccessService.isLocationEnabled()) {
      return;
    }

    // Fast path: App Info → Never / deny is usually visible via
    // checkPermissions immediately (no warmup, no GPS probe) so the UI
    // and popup appear without a white-screen hang.
    const quickReason = await this._locationAccessService.getOsLocationBlockReason({
      probeIfGranted: false,
    });

    if (quickReason !== 'none') {
      this._locationAccessService.notifyOsLocationBlocked(quickReason);
      await this.promptOsLocationUnavailableNotice();
      return;
    }

    // Device Location OFF can still report granted briefly after Control
    // Center — short settle + GPS probe only for that flaky case.
    await this.waitForLocationServicesWarmup();
    const blockReason = await this._locationAccessService.getOsLocationBlockReason({
      probeIfGranted: true,
    });

    if (blockReason !== 'none') {
      this._locationAccessService.notifyOsLocationBlocked(blockReason);
      await this.promptOsLocationUnavailableNotice();
      return;
    }

    await this._locationAccessService.reconcileLocationState();
    await this._locationAccessService.refreshGpsLocationWhenEnabled();

    if (this._locationAccessService.peekOsUnavailableNoticePending()) {
      await this.promptOsLocationUnavailableNotice();
    }
  }

  private async promptLocationAccessIfNeeded(): Promise<void> {
    if (this._locationAccessService.consumeOsUnavailableNoticePending()) {
      await this.promptOsLocationUnavailableNotice();
      return;
    }

    if (!this._locationAccessService.shouldPromptLocationAccessOnLaunch()) {
      return;
    }

    if (this._router.url.includes('/home/profile/location-visibility')) {
      return;
    }

    const topModal = await this.modalController.getTop();

    if (topModal?.id === 'location-permission-modal') {
      return;
    }

    // First ask only — full Enable location modal.
    await this.requestLocationPermission();
  }

  private async promptOsLocationUnavailableNotice(): Promise<void> {
    if (this.#isHandlingOsLocationNotice) {
      return;
    }

    if (this._router.url.includes('/home/profile/location-visibility')) {
      this._locationAccessService.consumeOsUnavailableNoticePending();
      return;
    }

    const topModal = await this.modalController.getTop();

    if (topModal?.id === 'location-permission-modal' || topModal?.id === 'device-location-off-alert') {
      return;
    }

    this.#isHandlingOsLocationNotice = true;
    this._locationAccessService.consumeOsUnavailableNoticePending();

    try {
      const blockReason = this._locationAccessService.getLastOsLocationBlockReason();
      const modal = await this.modalController.create({
        id: 'device-location-off-alert',
        cssClass: 'device-location-off-alert',
        component: DeviceLocationOffAlertComponent,
        componentProps: {
          blockReason: blockReason === 'permission_denied' ? 'permission_denied' : 'location_services_disabled',
        },
        backdropDismiss: true,
        showBackdrop: true,
      });
      await modal.present();
      const result = await modal.onDidDismiss<{
        blockReason?: EnableLocationFailureReason;
      }>();

      if (result.role === 'open_settings') {
        const reason =
          result.data?.blockReason ??
          this._locationAccessService.getLastEnableFailureReason() ??
          'location_services_disabled';

        if (reason === 'location_services_disabled') {
          await this._locationAccessService.openDeviceLocationSettings();
        } else {
          await this._locationAccessService.openAppLocationPermissionSettings();
        }
      }
    } catch (error) {
      console.warn('Failed to present device location off alert', error);
    } finally {
      this.#isHandlingOsLocationNotice = false;
    }
  }

  private hideTabBar(): void {
    const tabBar = document.getElementById('app-tab-bar');
    if (tabBar !== null) {
      tabBar.style.display = 'none';
    }
  }

  private showTabBar(): void {
    const tabBar = document.getElementById('app-tab-bar');
    if (tabBar !== null) {
      tabBar.style.display = 'flex';
    }
  }
}
